import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import { GameBackdrop, GameSubtitle } from '@/features/stadium/GameBackdrop'
import type { RGNEvent } from '@/lib/api/schemas'

/**
 * TEASER phase — pre-event countdown screen.
 *
 * Centre composition: big stacked countdown (DAYS · HRS · MIN · SEC),
 * tournament title above, entrant count + prize pool below. The
 * countdown ticks every second locally — `useStadiumData` doesn't
 * re-poll just for the countdown, the seconds-tick lives inside this
 * component so the bracket polling stays at 5 s.
 *
 * Anchoring on `event.checkInOpensAt` (when present) — that's the
 * actually-actionable moment for entrants — and falling back to
 * `event.startTime` otherwise. Renders a sub-line explaining which
 * moment the countdown targets.
 *
 * No expensive animations here. The countdown numbers are large +
 * legible; movement comes from the seconds digit ticking. A subtle
 * pulse on the colon separators (CSS keyframe in stadium.css) adds
 * the "broadcast clock" feel without burning frames.
 */
export function TeaserPhase({ event }: { event: RGNEvent }) {
  const targetMs = pickTarget(event)
  const remaining = useCountdown(targetMs)
  const isPaid = (event.entryFeePence ?? 0) > 0

  return (
    <div className="relative flex h-full flex-col items-center justify-center gap-[clamp(20px,3vh,48px)] px-[clamp(20px,3vw,60px)] text-center">
      <GameBackdrop event={event} />

      <motion.span
        initial={{ opacity: 0, transform: 'translateY(-12px)' }}
        animate={{ opacity: 1, transform: 'translateY(0px)' }}
        transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
        className="font-display relative font-extrabold uppercase"
        style={{
          color: 'var(--tv-primary)',
          letterSpacing: '0.3em',
          fontSize: 'clamp(0.85rem, 1.2vw, 1.2rem)',
        }}
      >
        Starts soon
      </motion.span>

      <div className="relative flex flex-col items-center">
        <h2
          className="font-display font-black leading-[0.95] tracking-tight"
          style={{ fontSize: 'clamp(2.5rem, 5vw, 5.5rem)', maxWidth: '20ch' }}
        >
          {event.title || 'Tournament'}
        </h2>
        <GameSubtitle event={event} />
      </div>

      <div className="relative">
        <Countdown remaining={remaining} />
      </div>

      <p
        className="relative font-display font-medium uppercase"
        style={{
          color: 'var(--tv-fg-muted)',
          letterSpacing: '0.2em',
          fontSize: 'clamp(0.8rem, 1vw, 1.1rem)',
        }}
      >
        {targetMs === toMs(event.checkInOpensAt)
          ? 'Until check-in opens'
          : 'Until first whistle'}
      </p>

      {(event.attendees?.length ?? event.attendeeCount ?? 0) > 0 || isPaid ? (
        <div className="relative mt-6 flex flex-wrap items-center justify-center gap-[clamp(20px,3vw,48px)]">
          {(event.attendees?.length ?? event.attendeeCount ?? 0) > 0 && (
            <Fact
              label="Entrants"
              value={`${event.attendees?.length ?? event.attendeeCount}${event.maxAttendees ? ` / ${event.maxAttendees}` : ''}`}
            />
          )}
          {isPaid && (
            <Fact
              label="Prize pool"
              value={`£${((event.prizePoolPence ?? 0) / 100).toFixed(0)}`}
              accent="var(--tv-gold)"
            />
          )}
          {event.bestOf && event.bestOf > 1 && (
            <Fact label="Format" value={`Bo${event.bestOf}`} />
          )}
        </div>
      ) : null}
    </div>
  )
}

function Countdown({ remaining }: { remaining: CountdownParts }) {
  // If we don't have a target (event has no startTime / checkInOpensAt
  // — shouldn't happen in practice) render a placeholder instead of
  // dashes which would read as "broken clock".
  if (remaining.kind === 'unknown') {
    return (
      <span
        className="font-display font-medium"
        style={{ color: 'var(--tv-fg-subtle)', fontSize: 'clamp(1rem, 1.3vw, 1.4rem)' }}
      >
        Awaiting schedule…
      </span>
    )
  }
  if (remaining.kind === 'past') {
    return (
      <span
        className="font-display font-extrabold uppercase"
        style={{ color: 'var(--tv-live)', fontSize: 'clamp(2rem, 3vw, 3.5rem)', letterSpacing: '0.2em' }}
      >
        Starting now
      </span>
    )
  }
  // Spacing: gap-2vw between units + a colon Sep between each pair.
  // Critical that DAYS↔HRS gets a Sep too — without it "1" + "07" reads
  // as "107" instead of "1 day, 7 hours". When days = 0 we drop the
  // unit entirely so the display narrows to HH:MM:SS.
  return (
    <div className="flex items-baseline gap-[clamp(12px,1.4vw,24px)]">
      {remaining.days > 0 && (
        <>
          <CountdownUnit value={remaining.days} label="DAYS" />
          <Sep />
        </>
      )}
      <CountdownUnit value={remaining.hours} label="HRS" pad />
      <Sep />
      <CountdownUnit value={remaining.minutes} label="MIN" pad />
      <Sep />
      <CountdownUnit value={remaining.seconds} label="SEC" pad />
    </div>
  )
}

function CountdownUnit({
  value,
  label,
  pad = false,
}: {
  value: number
  label: string
  pad?: boolean
}) {
  const display = pad ? String(value).padStart(2, '0') : String(value)
  return (
    <div className="flex flex-col items-center">
      <span
        className="font-display font-black leading-none tabular-nums"
        style={{
          fontSize: 'clamp(4rem, 9vw, 9rem)',
          color: 'var(--tv-fg)',
        }}
      >
        {display}
      </span>
      <span
        className="font-display font-bold uppercase"
        style={{
          color: 'var(--tv-fg-subtle)',
          fontSize: 'clamp(0.7rem, 0.85vw, 0.9rem)',
          letterSpacing: '0.3em',
          marginTop: 'clamp(4px, 0.5vh, 8px)',
        }}
      >
        {label}
      </span>
    </div>
  )
}

function Sep() {
  return (
    <span
      className="stadium-live-dot font-display font-black leading-none"
      style={{
        fontSize: 'clamp(3rem, 7vw, 7rem)',
        color: 'var(--tv-primary)',
        marginBottom: 'clamp(20px,2vw,40px)',
      }}
      aria-hidden
    >
      :
    </span>
  )
}

function Fact({
  label,
  value,
  accent = 'var(--tv-fg)',
}: {
  label: string
  value: string
  accent?: string
}) {
  return (
    <div className="flex flex-col items-center">
      <span
        className="font-display font-bold uppercase"
        style={{
          color: 'var(--tv-fg-subtle)',
          letterSpacing: '0.22em',
          fontSize: 'clamp(0.65rem, 0.85vw, 0.85rem)',
        }}
      >
        {label}
      </span>
      <span
        className="font-display font-extrabold tabular-nums"
        style={{
          color: accent,
          fontSize: 'clamp(1.5rem, 2.4vw, 2.5rem)',
          marginTop: 'clamp(2px, 0.3vh, 6px)',
        }}
      >
        {value}
      </span>
    </div>
  )
}

// ─── countdown logic ────────────────────────────────────────────────

type CountdownParts =
  | { kind: 'ticking'; days: number; hours: number; minutes: number; seconds: number }
  | { kind: 'past' }
  | { kind: 'unknown' }

function pickTarget(event: RGNEvent): number | null {
  return toMs(event.checkInOpensAt) ?? toMs(event.startTime) ?? null
}

function useCountdown(targetMs: number | null): CountdownParts {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  if (targetMs == null) return { kind: 'unknown' }
  const remaining = targetMs - now
  if (remaining <= 0) return { kind: 'past' }
  const seconds = Math.floor(remaining / 1000) % 60
  const minutes = Math.floor(remaining / 60_000) % 60
  const hours   = Math.floor(remaining / 3_600_000) % 24
  const days    = Math.floor(remaining / 86_400_000)
  return { kind: 'ticking', days, hours, minutes, seconds }
}

function toMs(v: number | string | null | undefined): number | null {
  if (v == null) return null
  if (typeof v === 'number') return v
  const parsed = Date.parse(v)
  return Number.isFinite(parsed) ? parsed : null
}
